// What has really happened on a project - the tasks that are done or under way - and what
// that means for moving the project's dates. Work that has started stays where it happened:
// the project can't start after it or end before it, and a shift only moves what hasn't
// started. This goes by the plan (the tasks); timesheets don't count.

import { addDays, differenceInCalendarDays, endOfDay, startOfDay } from "date-fns"
import { toZonedTime } from "date-fns-tz"

export const isTaskDone = (ai) => ai?.status === "done"

/**
 * Whether a task is done or under way: marked done, has progress, or sits in a board
 * column past the first one. A task that is late but untouched hasn't started.
 *
 * @param ai {object} The task
 * @param [mission] {object} Its project, for the board columns
 */
export const isTaskStarted = (ai, mission) => {
    if (!ai) return false
    if (isTaskDone(ai) || Number(ai.percentComplete) > 0) return true

    return Boolean(ai.col) && (mission?.cols || []).findIndex((col) => col.id === ai.col) > 0
}

/** A task's planned days. Board tasks have a week rather than dates. */
export const getTaskDays = (ai = {}) => {
    const first = ai.startDate || ai.week

    return {
        start: first ? startOfDay(toZonedTime(first)) : null,
        end: ai.endDate
            ? startOfDay(toZonedTime(ai.endDate))
            : ai.week
            ? startOfDay(addDays(toZonedTime(ai.week), 6))
            : null,
    }
}

const allTasks = (mission) =>
    (mission?.planItems || []).flatMap((p) => p.actions || []).flatMap((a) => a?.actionItems || [])

/**
 * How far the project's dates can go, given the work that has started.
 *
 * @param mission {object}
 * @param [tasks] {object[]} Its tasks, when they aren't on the mission
 * @return {{ latestStart: {date: Date, task: object}|null, earliestEnd: {date: Date, task: object}|null }}
 *   latestStart: the project can't start after this - the first task that is done or under way.
 *   earliestEnd: the project can't end before this - the last day of the last completed task.
 */
export const getProjectDateLimits = (mission, tasks = allTasks(mission)) => {
    let latestStart = null
    let earliestEnd = null

    tasks.forEach((task) => {
        if (!task || task.metaType === 4) return

        const { start, end } = getTaskDays(task)

        if (start && isTaskStarted(task, mission) && (!latestStart || start < latestStart.date)) {
            latestStart = { date: start, task }
        }

        if (isTaskDone(task) && (end || start) && (!earliestEnd || (end || start) > earliestEnd.date)) {
            earliestEnd = { date: end || start, task }
        }
    })

    return { latestStart, earliestEnd }
}

/**
 * Whether a project date change runs into work that has started. Only a change that makes
 * things worse is refused - a project that already breaks the rule can still be edited.
 *
 * @param mission {object} The project as it is
 * @param change {object} The change - planStartDate and / or planEndDate
 * @return {{ field: "start"|"end", date: Date, task: object }|null}
 */
export const getDateChangeBlock = (mission, change = {}, tasks) => {
    if (!mission || mission.isTemplate || (!change.planStartDate && !change.planEndDate)) return null

    const { latestStart, earliestEnd } = getProjectDateLimits(mission, tasks)
    const day = (ms) => startOfDay(toZonedTime(ms))

    if (
        latestStart &&
        change.planStartDate &&
        mission.planStartDate &&
        differenceInCalendarDays(day(change.planStartDate), day(mission.planStartDate)) > 0 &&
        differenceInCalendarDays(day(change.planStartDate), latestStart.date) > 0
    ) {
        return { field: "start", ...latestStart }
    }

    if (
        earliestEnd &&
        change.planEndDate &&
        mission.planEndDate &&
        differenceInCalendarDays(day(change.planEndDate), day(mission.planEndDate)) < 0 &&
        differenceInCalendarDays(day(change.planEndDate), earliestEnd.date) < 0
    ) {
        return { field: "end", ...earliestEnd }
    }

    return null
}

/** Whether a plan item (role, cost line, equipment line) has started: its first day is today or earlier. */
export const hasPlanItemStarted = (pi = {}, today = new Date()) =>
    Boolean(pi.startDate) && startOfDay(toZonedTime(pi.startDate)) <= endOfDay(today)
